<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-6xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Truck class="w-5 h-5 text-[#F36421]" />
            {{ form.id ? 'Editar Tarifa Logística' : 'Nueva Tarifa de Despacho (LLEVO)' }}
          </h2>
          <p class="text-xs text-slate-500">Configuración de cobro por KM, paradas adicionales y modelo de reparto 75% Chofer / 25% Agencia.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Pestañas de Navegación -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-[#F36421] text-[#F36421] bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
        </button>
      </div>

      <!-- Cuerpo del Formulario -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="tierForm" @submit.prevent="handleSubmit" class="space-y-6" novalidate>

          <!-- ================================================================= -->
          <!-- PESTAÑA 1: INFORMACIÓN GENERAL & TIPO DE FLOTA -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'general'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <Tag class="w-4 h-4 text-[#F36421]" />
                Identificación de la Tarifa
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label :class="labelClasses">* Código Corto</label>
                  <input v-model="form.code" type="text" maxlength="8" :class="inputClasses" placeholder="Ej: MOTO-EXP" required />
                  <span class="text-[10px] text-slate-400">Máx. 8 caracteres para reportes y DTE.</span>
                </div>

                <div class="md:col-span-2">
                  <label :class="labelClasses">* Nombre del Servicio</label>
                  <input v-model="form.name" type="text" :class="inputClasses" placeholder="Ej: Moto Express LLEVO (Hasta 15kg)" required />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label :class="labelClasses">* Tipo de Vehículo / Flota</label>
                  <select v-model="form.service_type" :class="inputClasses">
                    <option value="MOTO_EXPRESS">🛵 MOTO EXPRESS</option>
                    <option value="CARRO_CARGA">🚗 CARRO / HATCHBACK</option>
                    <option value="CAMIONETA_B2B">🛻 CAMIONETA / PICK-UP</option>
                    <option value="CAMION_PESADO">🚛 CAMIÓN PESADO</option>
                  </select>
                </div>

                <div>
                  <label :class="labelClasses">Categoría / Uso Operativo</label>
                  <select v-model="form.category" :class="inputClasses">
                    <option value="URBANO">DESPACHO URBANO</option>
                    <option value="INTERMUNICIPAL">INTERMUNICIPAL / CARRETERA</option>
                    <option value="CORPORATIVO">EXCLUSIVO B2B</option>
                    <option value="EXPRESS">EMERGENCIA / PRIORIDAD</option>
                  </select>
                </div>

                <div>
                  <label :class="labelClasses">Estado del Servicio</label>
                  <select v-model="form.status" :class="inputClasses">
                    <option value="ACTIVE">ACTIVO / DISPONIBLE</option>
                    <option value="INACTIVE">INACTIVO / SUSPENDIDO</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 2: TARIFARIO, DISTANCIAS & COMISIÓN AGENCIA (75/25) -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'pricing'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <h3 :class="[sectionTitleClasses, 'mb-0']">
                  <Navigation class="w-4 h-4 text-[#F36421]" />
                  Esquema de Cobro & Reparto de Ganancias (C$ / USD)
                </h3>
                <div class="flex items-center gap-2 text-xs bg-white dark:bg-slate-800 px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span class="text-slate-500 font-bold">Tasa de Cambio:</span>
                  <span class="font-mono font-bold text-emerald-600">1 USD = {{ exchangeRate }} C$</span>
                </div>
              </div>

              <!-- Parámetros Principales de la Tarifa -->
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Tarifa Base -->
                <div class="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <label :class="labelClasses">* Tarifa Base (0 - 2.5 KM)</label>
                  <div class="relative mt-1">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">C$</span>
                    <input v-model="form.base_fare" type="number" step="0.5" min="0" :class="[inputClasses, 'pl-8 font-mono font-bold text-slate-800 dark:text-white']" placeholder="45.00" required />
                  </div>
                  <p class="text-[10px] text-slate-400 mt-1">Banderazo mínimo del envío.</p>
                </div>

                <!-- Precio por Kilómetro Adicional -->
                <div class="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <label :class="labelClasses">* Precio x KM (> 2.5 KM)</label>
                  <div class="relative mt-1">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">C$</span>
                    <input v-model="form.price_per_km" type="number" step="0.5" min="0" :class="[inputClasses, 'pl-8 font-mono font-bold text-emerald-600']" placeholder="10.00" required />
                  </div>
                  <p class="text-[10px] text-slate-400 mt-1">Se suma por cada KM extra recorrido.</p>
                </div>

                <!-- Precio por Parada Adicional -->
                <div class="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <label :class="labelClasses">* Parada Adicional (Multipunto)</label>
                  <div class="relative mt-1">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">C$</span>
                    <input v-model="form.price_per_stop" type="number" step="0.5" min="0" :class="[inputClasses, 'pl-8 font-mono font-bold text-slate-800 dark:text-white']" placeholder="20.00" required />
                  </div>
                  <p class="text-[10px] text-slate-400 mt-1">Cobro por segundo o tercer punto.</p>
                </div>

                <!-- % Comisión de Agencia (Ganancia LLEVO) -->
                <div class="p-3 bg-orange-50/50 dark:bg-orange-950/10 rounded-xl border border-[#F36421]/40">
                  <label :class="labelClasses">* Ganancia Agencia LLEVO (%)</label>
                  <div class="relative mt-1">
                    <input v-model="form.commission_rate" type="number" step="1" min="0" max="100" :class="[inputClasses, 'pr-8 font-mono font-bold text-[#F36421]']" placeholder="25" required />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-[#F36421] font-bold">%</span>
                  </div>
                  <p class="text-[10px] text-slate-500 mt-1 font-semibold">El {{ 100 - (form.commission_rate || 0) }}% restante se paga al chofer.</p>
                </div>
              </div>

              <!-- Rangos de Distancia -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label :class="labelClasses">Distancia Base Incluida (KM)</label>
                  <input v-model="form.min_distance_km" type="number" step="0.5" min="0" :class="inputClasses" placeholder="2.5" />
                  <span class="text-[10px] text-slate-400">Kilómetros cubiertos por la Tarifa Base (Ej: 2.5 km).</span>
                </div>
                <div>
                  <label :class="labelClasses">Distancia Máxima Permitida (KM)</label>
                  <input v-model="form.max_distance_km" type="number" step="1" min="0" :class="inputClasses" placeholder="15" />
                  <span class="text-[10px] text-slate-400">Límite operativo de esta tarifa.</span>
                </div>
              </div>
            </div>

            <!-- TABLA SIMULADORA EN TIEMPO REAL (ESQUEMA LLEVO) -->
            <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
              <div class="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                <span class="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  📊 Proyección de Ingresos en Vivo (Lógica de Negocio LLEVO)
                </span>
                <span class="text-[10px] font-mono font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded">
                  Reparto: {{ 100 - (form.commission_rate || 0) }}% Chofer | {{ form.commission_rate || 0 }}% Agencia
                </span>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-xs text-left">
                  <thead class="text-[10px] text-slate-400 uppercase bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th class="px-4 py-2.5 font-bold">Distancia del Envío</th>
                      <th class="px-4 py-2.5 font-bold text-right">Cobro Total (Cliente)</th>
                      <th class="px-4 py-2.5 font-bold text-right text-blue-600">Pago Motorizado ({{ 100 - (form.commission_rate || 0) }}%)</th>
                      <th class="px-4 py-2.5 font-bold text-right text-[#F36421]">Ganancia Agencia ({{ form.commission_rate || 0 }}%)</th>
                      <th class="px-4 py-2.5 font-bold text-right text-emerald-600">Equiv. USD (Agencia)</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                    <tr v-for="sim in simulatedTable" :key="sim.label" class="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td class="px-4 py-2.5 font-sans font-bold text-slate-700 dark:text-slate-300">{{ sim.label }}</td>
                      <td class="px-4 py-2.5 text-right font-bold text-slate-900 dark:text-white">{{ sim.total }} C$</td>
                      <td class="px-4 py-2.5 text-right font-bold text-blue-600 dark:text-blue-400">{{ sim.driver }} C$</td>
                      <td class="px-4 py-2.5 text-right font-bold text-[#F36421]">{{ sim.agency }} C$</td>
                      <td class="px-4 py-2.5 text-right font-bold text-emerald-600">${{ sim.usd }} USD</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 3: CAPACIDAD DE CARGA & RECARGOS CLIMÁTICOS -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'capacity'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <Package class="w-4 h-4 text-[#F36421]" />
                Límites de Peso & Capacidad
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label :class="labelClasses">Peso Mínimo Permitido (KG)</label>
                  <input v-model="form.min_weight_kg" type="number" min="0" :class="inputClasses" />
                </div>
                <div>
                  <label :class="labelClasses">Peso Máximo por Unidad (KG)</label>
                  <input v-model="form.max_weight_kg" type="number" min="1" :class="inputClasses" />
                  <span class="text-[10px] text-slate-400">Ej: 15kg para Moto, 100kg para Carro, 750kg para Camioneta.</span>
                </div>
              </div>
            </div>

            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <Percent class="w-4 h-4 text-emerald-600" />
                Recargos Operativos Adicionales (100% Para el Motorizado)
              </h3>
              <p class="text-xs text-slate-500 mb-4">
                Estos recargos motivan a tu flota a trabajar en condiciones difíciles sin alterar tu porcentaje base.
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Recargo por Lluvia -->
                <div>
                  <label :class="labelClasses">Recargo por Lluvia / Mal Clima (C$)</label>
                  <input v-model="form.rain_surcharge" type="number" step="5" min="0" :class="inputClasses" placeholder="0" />
                  <span class="text-[10px] text-slate-400">Monto fijo en Córdobas que se suma cuando llueve fuerte.</span>
                </div>

                <!-- Recargo Nocturno -->
                <div>
                  <label :class="labelClasses">Recargo Horario Nocturno (C$)</label>
                  <input v-model="form.night_surcharge" type="number" step="5" min="0" :class="inputClasses" placeholder="0" />
                  <span class="text-[10px] text-slate-400">Aplica en despachos nocturnos (Ej: después de las 8:00 PM).</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 4: CONTABILIDAD & PARÁMETROS DTE -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'accounting'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <FileText class="w-4 h-4 text-[#F36421]" />
                Facturación Electrónica (DTE) y Contabilidad
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label :class="labelClasses">¿Emitir Factura Electrónica (DTE) en esta tarifa?</label>
                  <select v-model="form.emits_dte" :class="inputClasses">
                    <option :value="1">SÍ (Emisión DTE Automática)</option>
                    <option :value="0">NO (Régimen Simplificado / Interno)</option>
                  </select>
                </div>

                <div>
                  <label :class="labelClasses">Cuenta Contable de Ingreso</label>
                  <input v-model="form.accounting_account" type="text" :class="inputClasses" placeholder="Ej: 41010101 - Ingresos por Fletes en Moto" />
                </div>
              </div>

              <!-- Lista de Cargos Auxiliares (selected_charges) -->
              <div class="mt-4">
                <label :class="labelClasses">Cargos Auxiliares Aplicables (IDs o Códigos)</label>
                <input v-model="form.selected_charges" type="text" :class="inputClasses" placeholder="Ej: CARGO-ESPERA, CARGO-RETURN" />
                <span class="text-[10px] text-slate-400">Nombres o códigos de la tabla charges_and_fees separados por coma.</span>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- Pie del Modal -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex gap-3 w-full sm:w-auto justify-end ml-auto">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button v-if="activeTab !== 'accounting'" @click="nextTab" type="button" :class="btnPrimary">Siguiente</button>
          <button v-else type="submit" form="tierForm" :disabled="isSaving" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar Tarifa
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import {
  X, Save, Loader2, Truck, Navigation, Package, Percent,
  FileText, Tag
} from 'lucide-vue-next';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

const props = defineProps({ show: Boolean, tierToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const activeTab = ref('general');
const isSaving = ref(false);

// Tipo de Cambio oficial (Nicaragua: 36.62 C$ por USD approx)
const exchangeRate = 36.62;

const tabs = [
  { id: 'general', name: '1. Tipo & Identificación' },
  { id: 'pricing', name: '2. Cobros & Comisión (75/25)' },
  { id: 'capacity', name: '3. Pesos & Recargos' },
  { id: 'accounting', name: '4. Contabilidad & DTE' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800";
const sectionTitleClasses = "text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-[#F36421] rounded-xl hover:bg-[#d95214] shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

// Estado reactivo inicial configurado exactamente con la tabla de LLEVO (45 C$ base, 25% comisión, 10 C$/km extra)
const form = reactive({
  id: null,
  code: 'MOTO-EXP',
  name: 'Moto Express LLEVO',
  service_type: 'MOTO_EXPRESS',
  category: 'URBANO',
  status: 'ACTIVE',

  // Tarifario Logístico (Valores exactos de tu tabla)
  base_fare: 45.00,        // 45 C$ (0 - 2.5 KM)
  price_per_km: 10.00,     // 10 C$ por cada KM extra
  price_per_stop: 20.00,   // Costo por parada adicional
  min_distance_km: 2.50,   // Cubiertos por la tarifa base
  max_distance_km: 15.00,  // Límite operativo en ciudad

  // Capacidad y recargos
  min_weight_kg: 1,
  max_weight_kg: 15,
  commission_rate: 25.00,  // 25% Agencia / 75% Motorizado
  night_surcharge: 0.00,
  rain_surcharge: 0.00,

  // Contabilidad
  emits_dte: 1,
  accounting_account: '',
  selected_charges: '',

  // Campos Heredados / Requeridos por el Esquema de BD (Invisibles en UI)
  guaranty_type: 'NONE',
  funding_source_id: 'DEFAULT_LLEVO_FUNDING',
  interest_type: 'NORMAL',
  interest_rate: 0.00,
  moratory_rate: 0.00,
  min_amount: 0.00,
  max_amount: 99999.00,
  max_amount_new_customers: 99999.00,
  contract_type: 1,
  min_term_months: 1,
  max_term_months: 12,
  min_interest_rate: 0.00,
  max_interest_rate: 0.00
});

// ============================================================================
// CALCULADORA & TABLA DINÁMICA DE LA LÓGICA DE NEGOCIO (75% / 25%)
// ============================================================================
const simulatedTable = computed(() => {
  const base = Number(form.base_fare) || 0;
  const kmPrice = Number(form.price_per_km) || 0;
  const baseKm = Number(form.min_distance_km) || 2.5;
  const commPercent = (Number(form.commission_rate) || 0) / 100;
  const driverPercent = 1 - commPercent;

  // Hitos de distancia basados en la tabla del cliente: 2.5km, 3km, 4km, 6km, 8km, 10km
  const checkpoints = [
    { km: baseKm, label: `0.0 - ${baseKm} km (Base)` },
    { km: 3.0, label: '3.0 km' },
    { km: 4.0, label: '4.0 km' },
    { km: 6.0, label: '6.0 km' },
    { km: 8.0, label: '8.0 km' },
    { km: 10.0, label: '10.0 km (Largo)' }
  ];

  return checkpoints.map(pt => {
    // Si la distancia es menor o igual al rango base, cobra solo la base. Si no, suma los km extras.
    const extraKm = Math.max(0, pt.km - baseKm);
    const totalFare = base + (extraKm * kmPrice);

    const agencyEarnings = totalFare * commPercent;
    const driverEarnings = totalFare * driverPercent;
    const agencyUsd = agencyEarnings / exchangeRate;

    return {
      label: pt.label,
      total: totalFare.toFixed(0), // Enteros exactos en C$ como tu tabla
      driver: driverEarnings.toFixed(2).replace(/\.00$/, ''),
      agency: agencyEarnings.toFixed(2).replace(/\.00$/, ''),
      usd: agencyUsd.toFixed(2)
    };
  });
});

const nextTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) activeTab.value = tabs[currentIndex + 1].id;
};

// Cargar o reiniciar datos al abrir el modal
watch(() => props.show, (val) => {
  if (val) {
    activeTab.value = 'general';
    if (props.tierToEdit && Object.keys(props.tierToEdit).length > 0) {
      Object.assign(form, props.tierToEdit);
      form.emits_dte = props.tierToEdit.emits_dte === 1 || props.tierToEdit.emits_dte === true ? 1 : 0;
    } else {
      Object.assign(form, {
        id: null, code: 'MOTO-EXP', name: 'Moto Express LLEVO', service_type: 'MOTO_EXPRESS', category: 'URBANO', status: 'ACTIVE',
        base_fare: 45.00, price_per_km: 10.00, price_per_stop: 20.00, min_distance_km: 2.50, max_distance_km: 15.00,
        min_weight_kg: 1, max_weight_kg: 15, commission_rate: 25.00, night_surcharge: 0.00, rain_surcharge: 0.00,
        emits_dte: 1, accounting_account: '', selected_charges: '',
        guaranty_type: 'NONE', funding_source_id: 'DEFAULT_LLEVO_FUNDING', interest_type: 'NORMAL',
        interest_rate: 0.00, moratory_rate: 0.00, min_amount: 0.00, max_amount: 99999.00,
        max_amount_new_customers: 99999.00, contract_type: 1, min_term_months: 1, max_term_months: 12,
        min_interest_rate: 0.00, max_interest_rate: 0.00
      });
    }
  }
});

// Guardar tarifa en BD
const handleSubmit = async () => {
  if (!form.code || !form.name) {
    return showError('El Código y el Nombre de la Tarifa son campos requeridos.');
  }

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');

    // Mapeo seguro de tipos numéricos y empresa
    const payload = {
      ...form,
      company_id: user?.company || '1',
      base_fare: Number(form.base_fare) || 0,
      price_per_km: Number(form.price_per_km) || 0,
      price_per_stop: Number(form.price_per_stop) || 0,
      min_distance_km: Number(form.min_distance_km) || 0,
      max_distance_km: Number(form.max_distance_km) || 0,
      commission_rate: Number(form.commission_rate) || 25.00 // Pasa el 25% del margen a la tabla SQL
    };

    if (!form.id) {
      delete payload.id;
    }

    if (form.id) {
      await apiClient.put(`/service_tiers/${form.id}`, payload);
      showSuccess('Tarifa logística actualizada con éxito');
    } else {
      await apiClient.post('/service_tiers', payload);
      showSuccess('Tarifa configurada y guardada en el tarifario');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    console.error("Error al guardar tarifa de servicio:", error);
    showError('No se pudo guardar la tarifa en el servidor.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>